import type {
  LatestPipelineRun,
  OnboardingChannel,
  OnboardingStatusResponse,
} from '../types/onboarding-status.types';

/** A channel known to have a populated `latestPipelineRun`. Backend
 *  invariant: IN_PROGRESS channels always carry one, but the wire type
 *  permits `null` (NOT_STARTED case). Narrowing via this predicate makes
 *  the invariant local and keeps the reducer below null-safe. */
type ChannelWithRun = OnboardingChannel & { latestPipelineRun: LatestPipelineRun };

function hasPipelineRun(c: OnboardingChannel): c is ChannelWithRun {
  return c.latestPipelineRun !== null;
}

/** Step-3 route per catalog-source connector — the redirect resolver needs
 *  to pick a path before any route has loaded. Returns null for connectors
 *  that don't have a catalog-import flow (analytics-only, ads-only), which
 *  the caller treats as "send the user back to the onboarding root".
 *
 *  Exhaustive switch on `ConnectionType` so a future connector triggers a
 *  TS error here rather than silently defaulting to Google's Step 3
 *  (the regression the previous fallback introduced once M1 tightened
 *  `OnboardingChannel.connectorCode` to `ConnectionType`). */
function completeImportPathFor(
  connectorCode: OnboardingChannel['connectorCode'],
): '/onboarding/complete-import' | '/onboarding/integration/meta/complete-import' | null {
  switch (connectorCode) {
    case 'META_COMMERCE':
      return '/onboarding/integration/meta/complete-import';
    case 'GOOGLE_MERCHANT':
    case 'SHOPIFY':
    case 'MANUAL_UPLOAD':
      // GMC, Shopify, and Manual Upload all back the Google flow's catalog
      // source; Step 3 resolves the active catalog source from the user's
      // connections via `resolveCatalogSource`, so any of these connectors
      // lands on the same path.
      return '/onboarding/complete-import';
    case 'GOOGLE_ADS':
    case 'GA4_ANALYTICS':
    case 'GA4_REPORTING':
    case 'ADOBE_ANALYTICS':
    case 'SFTP':
    case 'FTP':
      // Non-catalog connectors don't run catalog-import workflows today, but
      // the wire type permits them with `importStatus === 'IN_PROGRESS'`.
      // Bail to the onboarding root rather than guessing a platform path.
      return null;
    default: {
      // Exhaustiveness assertion — TS errors here if a new ConnectionType
      // is added without extending this switch. We RETURN null (not throw)
      // because the runtime blast radius matters: this function runs in
      // the post-login `beforeLoad`, and a throw would kill the redirect
      // (caught by the route error boundary, leaving the user unable to
      // log in). Returning null falls back to `/onboarding`, which is
      // degraded but functional. The TS error still catches the drift at
      // compile time — we want compile-time strict, runtime forgiving.
      const _exhaustive: never = connectorCode;
      void _exhaustive;
      return null;
    }
  }
}

/** Where the post-login index route should send the user. `workflowId` is
 *  only present for the `complete-import` target. The Step-3 path is
 *  per-connector so a Meta IN_PROGRESS workflow lands on Meta's Step 3 tree
 *  rather than Google's (where source-label copy + retry connectorCode would
 *  be wrong). */
export type OnboardingRedirect =
  | { to: '/onboarding' }
  | {
      to: '/onboarding/complete-import' | '/onboarding/integration/meta/complete-import';
      workflowId: string;
    };

/**
 * Translate the aggregated onboarding status into the next route. Rules:
 *   1. No channel connected → start the onboarding flow.
 *   2. At least one channel has `importStatus === 'COMPLETE'` → send the user
 *      to `/onboarding` so they always enter through the onboarding hub rather
 *      than jumping straight to experiments on a bare post-login landing.
 *   3. Otherwise, if any channel is `IN_PROGRESS` → poll the most-recently
 *      started workflow on the complete-import page.
 *   4. Otherwise (connected but every channel is `NOT_STARTED`, or the
 *      channels array is empty) → back to onboarding so the user can kick
 *      off the import.
 */
export function resolveOnboardingRedirect(status: OnboardingStatusResponse): OnboardingRedirect {
  if (!status.connectedToChannel) return { to: '/onboarding' };

  const { channels } = status;
  if (channels.some((c) => c.importStatus === 'COMPLETE')) {
    return { to: '/onboarding' };
  }

  // Filter on both `IN_PROGRESS` status AND populated pipeline run so the
  // reducer below has non-null `latestPipelineRun`. In practice the two
  // conditions co-occur (backend invariant) but the wire type allows the
  // `null` slot, and we shouldn't crash if the backend ever drifts.
  const inProgress = channels.filter(
    (c): c is ChannelWithRun => c.importStatus === 'IN_PROGRESS' && hasPipelineRun(c),
  );
  if (inProgress.length > 0) {
    const mostRecent = inProgress.reduce((latest, c) =>
      Date.parse(c.latestPipelineRun.startedAt) > Date.parse(latest.latestPipelineRun.startedAt)
        ? c
        : latest,
    );
    const completeImportPath = completeImportPathFor(mostRecent.connectorCode);
    if (completeImportPath) {
      return {
        to: completeImportPath,
        workflowId: mostRecent.latestPipelineRun.workflowId,
      };
    }
    // Connector has no catalog-import Step 3 (analytics/ads); fall through.
    return { to: '/onboarding' };
  }

  return { to: '/onboarding' };
}

/** The most-recently-started catalog import that is currently running, plus the
 *  Step-3 route to reach it. Distinct from {@link resolveOnboardingRedirect}:
 *  this does NOT short-circuit on a COMPLETE channel — the progress widget
 *  tracks any still-running import regardless of sibling channels — and it
 *  carries the `connectorCode` so callers can resolve a source label. */
export interface ActiveCatalogImport {
  workflowId: string;
  to: '/onboarding/complete-import' | '/onboarding/integration/meta/complete-import';
  connectorCode: OnboardingChannel['connectorCode'];
}

/**
 * The active (IN_PROGRESS) catalog import to surface in the global progress
 * widget, or `null` when none is running / the running channel has no
 * catalog-import Step 3 (analytics/ads). Mirrors the IN_PROGRESS reduction in
 * `resolveOnboardingRedirect` but is intended for a passive indicator, so it
 * intentionally ignores the "any channel COMPLETE" hub-redirect rule.
 */
export function resolveActiveCatalogImport(
  status: OnboardingStatusResponse,
): ActiveCatalogImport | null {
  const inProgress = status.channels.filter(
    (c): c is ChannelWithRun => c.importStatus === 'IN_PROGRESS' && hasPipelineRun(c),
  );
  if (inProgress.length === 0) return null;

  const mostRecent = inProgress.reduce((latest, c) =>
    Date.parse(c.latestPipelineRun.startedAt) > Date.parse(latest.latestPipelineRun.startedAt)
      ? c
      : latest,
  );
  const to = completeImportPathFor(mostRecent.connectorCode);
  if (!to) return null;
  return {
    workflowId: mostRecent.latestPipelineRun.workflowId,
    to,
    connectorCode: mostRecent.connectorCode,
  };
}

/**
 * Shape of the `redirect()` argument that the post-login index route throws.
 * `search.workflowId` is attached only for complete-import variants — both
 * the bare Google literal and the Meta-tree literal carry one because they
 * both poll a workflow on Step 3.
 */
export type OnboardingRedirectArgs =
  | { to: '/onboarding' }
  | {
      to: '/onboarding/complete-import' | '/onboarding/integration/meta/complete-import';
      search: { workflowId: string };
    };

/**
 * Convert an `OnboardingRedirect` into the arg object the router's
 * `redirect()` expects. Extracted from the route's inline dispatcher so the
 * property "every variant with `workflowId` attaches it as `search`" can be
 * pinned by unit tests, independent of any consumer. The discriminant is the
 * presence of `workflowId` on the redirect, NOT the value of `to` — which
 * means a future widening of the union (a new complete-import literal) gets
 * routed correctly without a consumer update.
 */
export function toRedirectArgs(redirect: OnboardingRedirect): OnboardingRedirectArgs {
  if ('workflowId' in redirect) {
    return { to: redirect.to, search: { workflowId: redirect.workflowId } };
  }
  return { to: redirect.to };
}
